import {
  ChakraProvider,
  Flex,
  Box,
  chakra,
  Button,
  Center,
  Spinner,
  Heading,
  createStandaloneToast,
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import { saveAs } from "file-saver";
import { DinoCreatorForm } from "./form-components/DinoCreatorForm";
import { customTheme } from "./theme";

export const App = () => {
  const canvasWidth = 400;
  const canvasHeight = 400;

  const dinoFullResSize = 3000;

  const dinoCanvas = useRef<null | HTMLCanvasElement>(null);

  const [dinoNumber, setDinoNumber] = useState("8209");
  const [dinoImageUrl, setDinoImageUrl] = useState("");
  const [dinoType, setDinoType] = useState("og");

  const [isLoading, setIsLoading] = useState(false);

  const { ToastContainer, toast } = createStandaloneToast();

  function downloadCanvas() {
    // Get Current Canvas
    const currentCanvas = dinoCanvas.current;

    if (currentCanvas) {
      var dinoImage = currentCanvas.toDataURL("image/png");
      saveAs(dinoImage, "DapperDinoPFP.png");
    }
  }

  function loadImage(image: HTMLImageElement, imageURL: string) {
    return new Promise((resolve, reject) => {
      image.addEventListener("load", () => {
        resolve(image);
      });
      image.addEventListener("error", (err) => {
        reject(err);
      });
      image.crossOrigin = "anonymous";
      image.src = imageURL;
      return image;
    });
  }

  function writeImage(url: string, overlayUrl: string) {
    // Get Current Canvas
    const currentCanvas = dinoCanvas.current;
    const currentCanvasContext = currentCanvas?.getContext("2d");

    if (currentCanvas && currentCanvasContext) {
      // Set Height and Width and Clear Canvas
      currentCanvas.width = 3000;
      currentCanvas.height = 3000;
      currentCanvas.style.width = canvasWidth + "px";
      currentCanvas.style.height = canvasHeight + "px";
      currentCanvasContext.clearRect(0, 0, canvasHeight, canvasHeight);
    } else {
      return;
    }
    if (isLoading) {
      if (url.trim() === "" || overlayUrl === "") {
        setIsLoading(false);
      }
      return;
    }

    console.log("Writing to Canvas");
    console.log(`Overlay: ${overlayUrl}`);
    console.log(`Image: ${url}`);

    let image = new Image(dinoFullResSize, dinoFullResSize);
    let overlay = new Image(canvasWidth, canvasHeight);

    const images = [loadImage(image, url), loadImage(overlay, overlayUrl)];
    Promise.all(images)
      .then(() => {
        setIsLoading(false);
        currentCanvasContext.drawImage(
          image,
          0,
          0,
          dinoFullResSize,
          dinoFullResSize
        );
        currentCanvasContext.drawImage(
          overlay,
          0,
          0,
          dinoFullResSize,
          dinoFullResSize
        );
        console.log("Loaded Images.");
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        toast({
          title: `Something went wrong.`,
          description: `If issues persist, open a Dapper Dino support ticket.`,
          status: "error",
          isClosable: true,
        });
      });
  }

  const fetchDino = async (dinoNumber: string, dinoType: string) => {
    if (dinoNumber) {
      console.log("Dino Number: " + dinoNumber);
      let baseUrl = "https://nft-info.dapperdinos.com/";
      if (dinoType === "karma") {
        baseUrl += "karmacollective/";
      } else {
        baseUrl += "dino/";
      }

      const url = baseUrl + dinoNumber;
      await fetch(url, {
        method: "GET",
        mode: "cors",
      })
        .then((response) => {
          console.log(response);
          return response.json();
        })
        .then((dinoJson) => {
          console.log(dinoJson);
          setDinoImageUrl(dinoJson.image);
        })
        .catch((err) => {
          console.error("Failed to fetch Dino: " + err);
          toast({
            title: `Something went wrong.`,
            description: `If issues persist, open a Dapper Dino support ticket.`,
            status: "error",
            isClosable: true,
          });
        });
    }
  };

  useEffect(() => {
    if (dinoNumber) {
      console.log("Getting Dino Number: ", dinoNumber);
      fetchDino(dinoNumber.trim(), dinoType).catch(console.error);
    }
  }, [dinoNumber, dinoType]);

  useEffect(() => {
    setIsLoading(true);
    writeImage(dinoImageUrl, process.env.PUBLIC_URL + "/coinbase_shield.png");
  }, [dinoImageUrl]);

  return (
    <ChakraProvider theme={customTheme}>
      <Box
        minH="100vh"
        minW="100vw"
        bgImage={process.env.PUBLIC_URL + `/background.png`}
        bgRepeat="no-repeat"
        bgSize="cover"
      >
        <Center>
          <Flex flexDir="column" paddingTop="50px" paddingBottom="50px">
            <Heading as="h1" textColor="white" marginBottom="0.5vh">
              Dapper Dino Social Creator
            </Heading>
            <DinoCreatorForm
              mode="pfp"
              setDinoNumber={setDinoNumber}
              setDinoType={setDinoType}
            />

            {isLoading && (
              <Box
                w={canvasWidth}
                h={canvasHeight}
                bgColor="white"
                borderWidth="2px"
                color="white"
                borderStyle="solid"
              >
                <Spinner
                  color="blue"
                  size="xl"
                  thickness="4px"
                  marginTop={"178px"}
                  marginStart={"178px"}
                />
              </Box>
            )}
            <chakra.canvas
              ref={dinoCanvas}
              id="datalectCanvas"
              w={canvasWidth}
              h={canvasHeight}
              bgColor="white"
              borderWidth="2px"
              color="white"
              borderStyle="solid"
              borderRadius="base"
              hidden={isLoading}
            />

            <Button
              marginTop="2vh"
              colorScheme="blue"
              maxWidth="400px"
              borderWidth="2px"
              color="white"
              borderStyle="solid"
              onClick={downloadCanvas}
            >
              Download
            </Button>
          </Flex>
        </Center>
      </Box>
      <ToastContainer />
    </ChakraProvider>
  );
};
